/**
 * Панель участников комнаты — правая колонка, включается кнопкой в шапке.
 *
 * Состав: список участников (join + invite) с ролями и, начиная с этапа
 * «управление комнатами», МОДЕРАТОРСКИЕ ДЕЙСТВИЯ над ними: кик, бан,
 * смена роли (power level).
 *
 * ПРАВИЛО ВИДИМОСТИ КНОПОК: ни одна кнопка здесь не решает сама, показана
 * ли она. Решает permissions.ts (useRoomPermissions + canActOnMember) —
 * кнопка без соответствующего флага просто не рендерится. «Нажал и поймал
 * 403» в этой панели быть не должно.
 *
 * ДИЗАЙН ЧЕРНОВОЙ сознательно: список строк, разделитель, заголовок.
 */
import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent } from "react";
import type { Messages } from "../i18n/messages";
type TKey = keyof Messages;
import { tf, useT, useTF } from "../i18n";
import { RoomStateEvent, UserEvent, type MatrixClient, type Room } from "matrix-js-sdk";

import { useMatrixClient } from "../matrix/clientContext";
import { useAuthedAvatar } from "../matrix/media";
import { useRoomPermissions, canActOnMember } from "../matrix/permissions";
import {
  banUser,
  humanError,
  kickUser,
  membersOf,
  setPowerLevel,
  unbanUser,
  type MemberInfo,
} from "../matrix/roomActions";
import { Dialog, DialogButton, Field, useAsyncAction } from "./Dialog";
import { s, useNarrowScreen } from "./styles";
import { color, colorOf, font, radius, space } from "./theme";

/** Варианты роли в выпадающем списке. Числа — договорённость комнат стенда. */
const roleChoicesOf = (t: (k: TKey) => string) => [
  { level: 0, label: t("member.roleParticipant") },
  { level: 50, label: t("member.roleModerator") },
  { level: 100, label: t("member.admin") },
] as const;

export function MemberPanel({
  client,
  room,
  onClose,
  onInvite,
  canInvite,
  embedded = false,
}: {
  client: MatrixClient;
  room: Room;
  onClose: () => void;
  onInvite: () => void;
  canInvite: boolean;
  /**
   * Встроенный режим: список рисуется ВНУТРИ таба «Участники» на странице
   * комнаты, а не подменяет собой всю панель.
   *
   * Почему появился (Антон 10.09 17:25 «нужно месте а не весь фрейм»):
   * клик по табу выбрасывал со страницы комнаты целиком — исчезали и аватар
   * с названием, и ряд действий, и сама полоска табов. Человек терял контекст
   * и возможность переключиться на соседний таб: единственный путь назад — крестик.
   * Во встроенном режиме собственная шапка панели не рисуется (заголовок
   * и крестик уже есть у страницы, «Пригласить» — в ряду четырёх действий),
   * и контейнер не задаёт ширину 260 px и свою границу слева.
   */
  embedded?: boolean;
}) {
  const t = useT();
  const tfmt = useTF();
  // Состав комнаты меняется событиями состояния (m.room.member), а не
  // сообщениями. Без подписки на RoomStateEvent.Members только что
  // приглашённый человек не появился бы в панели до перезагрузки страницы —
  // и приглашение выглядело бы не сработавшим.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    client.on(RoomStateEvent.Members, bump);
    return () => {
      client.off(RoomStateEvent.Members, bump);
    };
  }, [client]);

  const members = useMemo(
    () => membersOf(client, room),
    // tick в зависимостях намеренно: он и есть сигнал «состав изменился».
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [client, room, tick],
  );

  const perms = useRoomPermissions(client, room);
  const myUserId = client.getUserId() ?? "";

  // Открытый диалог модерации. Один на всю панель: тип-сумма, как в App.tsx.
  const [action, setAction] = useState<
    | { kind: "kick"; member: MemberInfo }
    | { kind: "ban"; member: MemberInfo }
    | { kind: "unban"; userId: string }
    | { kind: "role"; member: MemberInfo }
    | null
  >(null);

  const joined = members.filter((m) => m.membership === "join");
  const invited = members.filter((m) => m.membership === "invite");
  const serverCount = room.getJoinedMemberCount();

  // Забаненные: room.getMembers() их не отдаёт (только join/invite с учётом
  // lazy load), читаем напрямую из currentState — там полный набор
  // m.room.member событий, включая ban.
  const banned = useMemo(() => {
    const events = room.currentState.getStateEvents("m.room.member");
    const list: { userId: string; name: string }[] = [];
    for (const ev of events) {
      const c = ev.getContent() as { membership?: string; displayname?: string };
      const stateKey = ev.getStateKey();
      if (c.membership === "ban" && typeof stateKey === "string") {
        list.push({
          userId: stateKey,
          name: c.displayname ?? stateKey,
        });
      }
    }
    return list;
    // tick: бан приходит как Members event, перечитываем вместе со списком.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, tick]);

  const Container = embedded ? "div" : "aside";
  // На узком экране (≤640px) панель во всю ширину, а не жёсткие 260px:
  // иначе список участников на телефоне сжимался до колонки с обрезанными
  // именами, а кнопки действий по наведению не помещались в строку.
  const narrow = useNarrowScreen();

  return (
    <Container
      style={
        embedded
          ? { display: "flex", flexDirection: "column", minHeight: 0 }
          : narrow
            ? {
                ...s.memberPanel,
                width: "100%",
                flex: "1 1 auto",
                borderLeft: "none",
              }
            : s.memberPanel
      }
      data-testid="member-panel"
      aria-label={embedded ? undefined : t("ui.uchastniki_komnaty")}
      /* alg-member-panel: на ≤640px мобильный CSS делает панель fixed на
         весь экран поверх ленты (спека 2026-09-20). */
      className={!embedded && narrow ? "alg-member-panel" : undefined}
    >
      {!embedded && (
        <div
          style={{
            ...s.roomHeader,
            display: "flex",
            alignItems: "center",
            gap: space.md,
          }}
        >
          <div style={{ flex: "1 1 auto", fontSize: font.body, fontWeight: font.weightBold }}>
            {tfmt("member.participants", { count: joined.length })}
          </div>
          {canInvite && (
            <button
              type="button"
              style={{ ...s.actionButton, transition: "background 0.12s" }}
              onClick={onInvite}
              data-testid="member-invite"
              onMouseEnter={(e) => {
                e.currentTarget.style.background = color.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = color.bg;
              }}
            >{t("common.inviteShort")}</button>
          )}
          <button
            type="button"
            style={{ ...s.actionButton, transition: "background 0.12s" }}
            onClick={onClose}
            data-testid="member-close"
            onMouseEnter={(e) => {
              e.currentTarget.style.background = color.hover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = color.bg;
            }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Во встроенном режиме СВОЕГО скролла нет: список течёт внутри страницы
          и прокручивается вместе с ней (Антон 17:33: «строится он должен в рамках
          вот этого фрейма»). Собственный overflow дал бы вторую полосу прокрутки
          внутри уже прокручиваемой страницы — два вложенных скролла дерутся за колёсико.
          Отдельной панелью (embedded=false) скролл свой — там он единственный. */}
      <div
        style={
          embedded
            ? { flex: "0 0 auto" }
            : { flex: "1 1 auto", overflowY: "auto", minHeight: 0 }
        }
      >
        {joined.map((m, i) => (
          <MemberRow
            key={m.userId}
            m={m}
            client={client}
            first={i === 0}
            self={m.userId === myUserId}
            canKick={perms.canKick && canActOnMember(client, room, m.userId, "kick")}
            canBan={perms.canBan && canActOnMember(client, room, m.userId, "ban")}
            canChangeRole={
              perms.canChangePowerLevels &&
              canActOnMember(client, room, m.userId, "changePowerLevel")
            }
            onKick={() => setAction({ kind: "kick", member: m })}
            onBan={() => setAction({ kind: "ban", member: m })}
            onChangeRole={() => setAction({ kind: "role", member: m })}
          />
        ))}

        {invited.length > 0 && (
          <>
            <div style={s.sectionLabel} data-testid="member-section-invited">
              {tfmt("member.invitedCount", { count: invited.length })}
            </div>
            {invited.map((m, i) => (
              <MemberRow
                key={m.userId}
                m={m}
                client={client}
                first={i === 0}
                self={m.userId === myUserId}
                canKick={perms.canKick && canActOnMember(client, room, m.userId, "kick")}
                canBan={perms.canBan && canActOnMember(client, room, m.userId, "ban")}
                canChangeRole={false}
                onKick={() => setAction({ kind: "kick", member: m })}
                onBan={() => setAction({ kind: "ban", member: m })}
                onChangeRole={() => {}}
              />
            ))}
          </>
        )}

        {banned.length > 0 && (
          <>
            <div style={s.sectionLabel} data-testid="member-section-banned">
              {tfmt("member.bannedCount", { count: banned.length })}
            </div>
            {banned.map((b, i) => (
              <div
                key={b.userId}
                style={{
                  ...s.memberRow,
                  ...(i === 0 ? { borderTop: "none" } : null),
                  display: "flex",
                  alignItems: "center",
                  gap: space.md,
                }}
                data-testid="member-banned-row"
                data-user-id={b.userId}
              >
                <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                  <div
                    style={{
                      fontSize: font.small,
                      color: color.text,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {b.name}
                  </div>
                  <div style={{ fontSize: font.micro, color: color.textDim }}>{b.userId}</div>
                </div>
                {perms.canBan && (
                  <button
                    type="button"
                    style={{ ...s.actionButton, transition: "background 0.12s" }}
                    onClick={() => setAction({ kind: "unban", userId: b.userId })}
                    data-testid="member-unban"
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = color.hover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = color.bg;
                    }}
                  >{t("member.unban")}</button>
                )}
              </div>
            ))}
          </>
        )}

        {members.length === 0 && (
          <div style={s.emptyHint}>{t("member.notLoaded")}</div>
        )}

        {/* Честная оговорка вместо молчаливо короткого списка: если сервер отдал
            не всё состояние комнаты, число вошедших по его версии и число строк
            здесь разойдутся, и человек должен это видеть. */}
        {serverCount !== joined.length && (
          <div style={s.emptyHint} data-testid="member-mismatch">
            {tfmt("member.mismatch", { serverCount, loaded: joined.length })}
          </div>
        )}
      </div>

      {action?.kind === "kick" && (
        <KickDialog
          client={client}
          room={room}
          member={action.member}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "ban" && (
        <BanDialog
          client={client}
          room={room}
          member={action.member}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "unban" && (
        <UnbanDialog
          client={client}
          room={room}
          userId={action.userId}
          onClose={() => setAction(null)}
        />
      )}
      {action?.kind === "role" && (
        <RoleDialog
          client={client}
          room={room}
          member={action.member}
          onClose={() => setAction(null)}
        />
      )}
    </Container>
  );
}

/**
 * Статус присутствия участника (m.presence), если сервер его отдаёт.
 *
 * ПОЧЕМУ НЕ ВСЕГДА userId: по образцу Антона вторая строка — это статус
 * («online» синим, «был недавно» серым), а не MXID. Но presence в Matrix
 * сервер может ОТКЛЮЧИТЬ (у Synapse — `presence.enabled: false`), и тогда
 * SDK честно сообщает всем `offline` без lastActiveAgo. Рисовать в такой
 * ситуации «online» всем — дезинформация, поэтому правило:
 *   - есть currentlyActive или presence != "offline" → показываем статус;
 *   - есть lastActiveAgo → «был …»;
 *   - ничего этого нет → сервер presence, скорее всего, не отдаёт, и мы
 *     честно оставляем userId, как было.
 */
function usePresence(client: MatrixClient, userId: string) {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    client.on(UserEvent.Presence, bump);
    return () => {
      client.off(UserEvent.Presence, bump);
    };
  }, [client]);

  // version нужен только как сигнал перечитать объект User — он живёт в
  // store клиента и мутируется на каждое presence-событие.
  void version;
  const user = client.getUser(userId);
  if (!user) return null;
  if (user.currentlyActive || (user.presence && user.presence !== "offline")) {
    return { kind: "online" as const };
  }
  const ago = user.lastActiveAgo;
  if (ago != null && ago > 0) return { kind: "seen" as const, ago };
  return null;
}

/** «был(а) N минут/часов назад» — компактно, без библиотек. */
function fmtLastActive(ms: number, t: (k: TKey) => string): string {
  const min = Math.round(ms / 60000);
  if (min < 1) return t("member.justNow");
  if (min < 60) return tf("member.lastActive", { count: min });
  const h = Math.round(min / 60);
  if (h < 24) return tf("member.lastActiveHours", { count: h });
  const d = Math.round(h / 24);
  return tf("member.lastActiveDays", { count: d });
}

function MemberRow({
  m,
  client,
  first,
  self,
  canKick,
  canBan,
  canChangeRole,
  onKick,
  onBan,
  onChangeRole,
}: {
  m: MemberInfo;
  client: MatrixClient;
  /** Первая строка списка: разделитель над ней не нужен (он МЕЖДУ строками). */
  first?: boolean;
  self: boolean;
  canKick: boolean;
  canBan: boolean;
  canChangeRole: boolean;
  onKick: () => void;
  onBan: () => void;
  onChangeRole: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  // Аватар участника — через авторизованную загрузку (см. media.ts).
  const avatarClient = useMatrixClient();
  const avatarSrc = useAuthedAvatar(avatarClient, m.avatarUrl);
  const role = m.powerLevel >= 100 ? "owner" : m.powerLevel >= 50 ? "admin" : null;
  const hasActions = !self && (canKick || canBan || canChangeRole);
  // Кнопки Роль/Кик/Бан показываем ТОЛЬКО при наведении на строку.
  // Почему: на скриншоте Антона 19:17 видно, что три кнопки на каждой из
  // 16 строк превращают список участников в стену управляющих элементов —
  // взгляд не находит ни имён, ни бейджей ролей. В образце (ref-01.png)
  // строка участника чистая: аватар, имя, статус, бейдж. Действия остаются
  // полностью доступными: наведение мышью, правый клик (контекстное меню)
  // и клавиатурный фокус внутри строки. На тач-экранах наведения нет —
  // поэтому кнопки показываются всегда при отсутствии hover-устройства
  // (см. coarsePointer ниже), иначе действия стали бы недостижимы.
  const [hovered, setHovered] = useState(false);
  const coarsePointer =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(hover: none)").matches;
  const showActions = hasActions && (hovered || coarsePointer);
  // Контекстное меню по правому клику (постановка Антона 15:42: «правый клик
  // на пользователе позволяет сменить его права»). Кнопки в строке
  // ОСТАЮТСЯ: правый клик неочевиден и недоступен на тач-экране, убирать
  // видимый путь к тому же действию нельзя.
  const [menu, setMenu] = useState<{ x: number; y: number } | null>(null);
  const presence = usePresence(client, m.userId);

  const openMenu = (e: ReactMouseEvent) => {
    if (!hasActions) return; // нет прав — нет и меню, вместо пустой рамки
    e.preventDefault();
    setMenu({ x: e.clientX, y: e.clientY });
  };

  return (
    <div
      style={{ ...s.memberRow, ...(first ? { borderTop: "none" } : null) }}
      data-testid="member-row"
      data-user-id={m.userId}
      onContextMenu={openMenu}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={(e) => {
        // Не гасим кнопки, пока фокус остаётся внутри строки, иначе
        // переход Tab'ом с «Роль» на «Кик» убирал бы цель под пальцами.
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHovered(false);
      }}
    >
      {menu && (
        <MemberContextMenu
          x={menu.x}
          y={menu.y}
          name={m.name}
          canChangeRole={canChangeRole}
          canKick={canKick}
          canBan={canBan}
          onChangeRole={onChangeRole}
          onKick={onKick}
          onBan={onBan}
          onClose={() => setMenu(null)}
        />
      )}
      {avatarSrc ? (
        <img src={avatarSrc} alt="" style={s.memberAvatar} />
      ) : (
        <div style={{ ...s.memberAvatar, background: colorOf(m.userId) }} aria-hidden>
          {m.initials}
        </div>
      )}
      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
        <div
          style={{
            // Имя 16px полужирным (образец Антона 10.09): раньше имя шло
            // тем же кеглем и весом, что id под ним, и обе строки сливались
            // в одну серую массу — взгляду не за что было зацепиться.
            fontSize: 16,
            fontWeight: font.weightBold,
            color: color.text,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {m.name}
          {self && <span style={{ color: color.textDim, fontWeight: 400 }}>{t("member.youSuffix")}</span>}
        </div>
        <div
          style={{
            // Вторая строка 14px серым (образец 10.09). Содержимое — статус
            // присутствия, ЕСЛИ сервер его отдаёт (см. usePresence); иначе
            // честный userId, как было. Фальшивый «online» не рисуем.
            fontSize: 14,
            color: presence?.kind === "online" ? color.accent : color.textDim,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {m.membership === "invite" ? (
            <>{tfmt("member.invitedUser", { user: m.userId })}</>
          ) : presence?.kind === "online" ? (
            "online"
          ) : presence?.kind === "seen" ? (
            fmtLastActive(presence.ago, t)
          ) : (
            m.userId
          )}
        </div>
      </div>
      {/* Роль — БЕЙДЖ справа от имени, как в образце Антона (ref-01.png):
          строчными, приглушённый фиолетовый текст на светло-фиолетовом фоне,
          pill, прижат вправо. PL100 → owner, PL50 → admin, у обычных
          участников бейджа нет вовсе — правая часть строки пустая. */}
      {role && (
        <span
          style={{
            flex: "0 0 auto",
            fontSize: font.small,
            fontWeight: font.weightNormal,
            color: color.roleBadgeText,
            background: color.roleBadgeBg,
            borderRadius: 10,
            padding: "4px 10px",
            lineHeight: 1.2,
            whiteSpace: "nowrap",
            textTransform: "lowercase",
          }}
          data-testid="member-role-badge"
        >
          {role}
        </span>
      )}
      {showActions && (
        <div style={{ display: "flex", gap: space.sm, flex: "0 0 auto" }}>
          {canChangeRole && (
            <button
              type="button"
              style={{ ...s.actionButton, transition: "background 0.12s, border-color 0.12s" }}
              onClick={onChangeRole}
              data-testid="member-role"
              title={t("member.changeRole")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = color.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = color.bg;
              }}
            >{t("member.role")}</button>
          )}
          {canKick && (
            <button
              type="button"
              style={{ ...s.actionButton, transition: "background 0.12s, border-color 0.12s" }}
              onClick={onKick}
              data-testid="member-kick"
              title={t("ui.isklyuchit_iz_komnaty")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = color.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = color.bg;
              }}
            >{t("member.kickShort")}</button>
          )}
          {canBan && (
            <button
              type="button"
              style={{ ...s.actionButton, color: color.danger, transition: "background 0.12s, border-color 0.12s" }}
              onClick={onBan}
              data-testid="member-ban"
              title={t("ui.zabanit")}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = color.hover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = color.bg;
              }}
            >{t("member.banShort")}</button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Контекстное меню участника — правый клик по строке.
 *
 * Позиционируется по курсору с прижатием к краю окна: у нижних строк списка
 * меню иначе уезжало бы за границу панели. Пункты — те же действия, что и
 * кнопки в строке, и открывают ТЕ ЖЕ диалоги подтверждения: второй путь к
 * действию не должен обходить подтверждение, иначе правый клик станет
 * опаснее кнопки.
 */
function MemberContextMenu({
  x,
  y,
  name,
  canChangeRole,
  canKick,
  canBan,
  onChangeRole,
  onKick,
  onBan,
  onClose,
}: {
  x: number;
  y: number;
  name: string;
  canChangeRole: boolean;
  canKick: boolean;
  canBan: boolean;
  onChangeRole: () => void;
  onKick: () => void;
  onBan: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const W = 210;
  const H = 132;
  const left = Math.min(x, window.innerWidth - W - 8);
  const top = Math.min(y, window.innerHeight - H - 8);

  const item = (
    label: string,
    onPick: () => void,
    danger = false,
  ) => (
    <button
      type="button"
      role="menuitem"
      onClick={() => {
        onClose();
        onPick();
      }}
      style={{
        display: "block",
        width: "100%",
        padding: "8px 12px",
        border: "none",
        background: "transparent",
        textAlign: "left",
        fontSize: font.small,
        color: danger ? color.danger : color.text,
        cursor: "pointer",
        transition: "background 0.12s",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = color.hover;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
      }}
      data-testid={`ctx-${label}`}
    >
      {label}
    </button>
  );

  return (
    <>
      {/* Подложка: клик мимо и правый клик мимо закрывают меню. */}
      <div
        style={{ position: "fixed", inset: 0, zIndex: 40 }}
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
      />
      <div
        role="menu"
        aria-label={tfmt("member.actionsFor", { name })}
        data-testid="member-context-menu"
        style={{
          position: "fixed",
          left,
          top,
          width: W,
          zIndex: 41,
          background: color.bg,
          border: `1px solid ${color.border}`,
          borderRadius: radius.md,
          boxShadow: "0 10px 28px rgba(20,24,31,.18)",
          overflow: "hidden",
          padding: "4px 0",
        }}
      >
        <div
          style={{
            padding: "4px 12px 6px",
            fontSize: font.micro,
            color: color.textDim,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {name}
        </div>
        {canChangeRole && item(t("ui.izmenit_prava"), onChangeRole)}
        {canKick && item(t("ui.isklyuchit_iz_komnaty"), onKick)}
        {canBan && item(t("ui.zabanit"), onBan, true)}
      </div>
    </>
  );
}

// --- Диалоги модерации ------------------------------------------------------

function KickDialog({
  client,
  room,
  member,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  member: MemberInfo;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [reason, setReason] = useState("");
  const { busy, error, run } = useAsyncAction(onClose);

  return (
    <Dialog
      title={tfmt("member.kickQuestion", { name: member.name })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="danger"
            disabled={busy}
            testId="kick-confirm"
            onClick={() =>
              run(async () => {
                try {
                  await kickUser(client, room.roomId, member.userId, reason || undefined);
                } catch (e) {
                  throw new Error(humanError(e));
                }
              })
            }
          >{t("member.kick")}</DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.text }}>
        {member.name}{t("member.kickWarnBody")}</div>
      <Field
        label={t("ui.prichina_neobyazatelno_vidna_drugim_uchast")}
        value={reason}
        onChange={setReason}
        placeholder={t("ui.naprimer_flud")}
        testId="kick-reason"
      />
    </Dialog>
  );
}

function BanDialog({
  client,
  room,
  member,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  member: MemberInfo;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [reason, setReason] = useState("");
  const { busy, error, run } = useAsyncAction(onClose);

  return (
    <Dialog
      title={tfmt("member.banQuestion", { name: member.name })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="danger"
            disabled={busy}
            testId="ban-confirm"
            onClick={() =>
              run(async () => {
                try {
                  await banUser(client, room.roomId, member.userId, reason || undefined);
                } catch (e) {
                  throw new Error(humanError(e));
                }
              })
            }
          >{t("ui.zabanit")}</DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.text }}>
        {t("member.banStricterHead")} {member.name} <b>{t("member.banCannotReturn")}</b>
        {t("member.banCannotReturnTail")}
      </div>
      <Field
        label={t("ui.prichina_neobyazatelno_vidna_drugim_uchast")}
        value={reason}
        onChange={setReason}
        placeholder={t("ui.naprimer_spam")}
        testId="ban-reason"
      />
    </Dialog>
  );
}

function UnbanDialog({
  client,
  room,
  userId,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  userId: string;
  onClose: () => void;
}) {
  const t = useT();
  const { busy, error, run } = useAsyncAction(onClose);

  return (
    <Dialog
      title={t("ui.snyat_ban")}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy}
            testId="unban-confirm"
            onClick={() =>
              run(async () => {
                try {
                  await unbanUser(client, room.roomId, userId);
                } catch (e) {
                  throw new Error(humanError(e));
                }
              })
            }
          >{t("member.unban")}</DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.text }}>
        {userId}{t("member.unbanWarn")}<b>{t("member.kickNoAutoReturn")}</b>{t("member.kickNoAutoReturnTail")}</div>
    </Dialog>
  );
}

function RoleDialog({
  client,
  room,
  member,
  onClose,
}: {
  client: MatrixClient;
  room: Room;
  member: MemberInfo;
  onClose: () => void;
}) {
  const t = useT();
  const tfmt = useTF();
  const [level, setLevel] = useState<number>(member.powerLevel);
  const { busy, error, run } = useAsyncAction(onClose);

  return (
    <Dialog
      title={tfmt("member.roleFor", { name: member.name })}
      onClose={onClose}
      error={error}
      footer={
        <>
          <DialogButton onClick={onClose} disabled={busy}>{t("common.cancel")}</DialogButton>
          <DialogButton
            kind="primary"
            disabled={busy || level === member.powerLevel}
            testId="role-confirm"
            onClick={() =>
              run(async () => {
                try {
                  await setPowerLevel(client, room, member.userId, level);
                } catch (e) {
                  throw new Error(humanError(e));
                }
              })
            }
          >{t("common.save")}</DialogButton>
        </>
      }
    >
      <div style={{ fontSize: font.small, color: color.text }}>
        {t("member.roleExplain")}
      </div>
      <label style={{ display: "block", fontSize: font.small, color: color.textDim }}>{t("member.powerLevel")}<select
          style={s.dialogInput}
          value={level}
          data-testid="role-select"
          onChange={(e) => setLevel(Number(e.target.value))}
        >
          {roleChoicesOf(t).map((r) => (
            <option key={r.level} value={r.level}>
              {r.label} ({r.level})
            </option>
          ))}
        </select>
      </label>
    </Dialog>
  );
}
